import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';
import io, { Socket } from 'socket.io-client';
import { logger } from '@/lib/logger';
import { Message } from 'ai';
import { ChatProgressStage } from '@/lib/types/chat';
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store'
import { getToken } from '@/app/actions/auth';
import { UserInfo } from '@/lib/types'

// Define missing interfaces
interface ChatProgress {
  chatId: string;
  stage: ChatProgressStage;
  progress: number;
  message?: string
}

interface SocialSearchProgress {
  userId: string;
  progress: number;
}

// Replace missing safe-storage with a simple implementation if not available
const safeStorage = {
  getItem: (key: string) => {
    try {
      return typeof window !== 'undefined' ? localStorage.getItem(key) : null;
    } catch (e) {
      console.error('Error accessing localStorage:', e);
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    try {
      if (typeof window !== 'undefined') localStorage.setItem(key, value);
    } catch (e) {
      console.error('Error setting localStorage:', e);
    }
  },
  removeItem: (key: string) => {
    try {
      if (typeof window !== 'undefined') localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  }
};

/**
 * Environment Configuration for WebSocket Connections
 *
 * This configuration replaces the previous import from '@/lib/env'.
 * We use Next.js environment variables directly (process.env) which:
 *
 * 1. Eliminates the dependency on an external env module
 * 2. Uses Next.js built-in environment handling which is more reliable
 * 3. Provides fallback values for all environment variables
 *
 * Environment variables used:
 * - NEXT_PUBLIC_WS_URL: Production WebSocket URL
 * - NEXT_PUBLIC_WS_STAGING_URL: Staging WebSocket URL
 * - NEXT_PUBLIC_WS_LB_URL: Load balancer WebSocket URL
 *
 * These environment variables should be defined in .env.local or through
 * your deployment platform (e.g., Vercel).
 */
const WEBSOCKET_ENV = {
  PRODUCTION_URL: process.env.NEXT_PUBLIC_WS_URL || 'wss://ws.mixus.ai',
  STAGING_URL: process.env.NEXT_PUBLIC_WS_STAGING_URL || 'wss://ec2-54-221-135-160.compute-1.amazonaws.com:3001',
  LOCAL_URL: 'ws://localhost:3001',
  LOAD_BALANCER_URL: process.env.NEXT_PUBLIC_WS_LB_URL || 'wss://mixus-websocket-v1-1778448387.us-east-1.elb.amazonaws.com:3001'
};

// Connection status types
export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting';

// Server key types
export type ServerKey = 'production' | 'staging' | 'loadBalancer' | 'local';

export const WS_SERVERS = {
  production: {
    label: 'Production & Development DB',
    url: WEBSOCKET_ENV.PRODUCTION_URL
  },
  staging: {
    label: 'EC2 Instance',
    url: WEBSOCKET_ENV.STAGING_URL
  },
  loadBalancer: {
    label: 'Load Balancer',
    url: WEBSOCKET_ENV.LOAD_BALANCER_URL
  },
  local: {
    label: 'Localhost',
    url: WEBSOCKET_ENV.LOCAL_URL
  }
} as const;

// Typing timeout - keep short for better UX
const TYPING_TIMEOUT = 3500;
// Check for expired typing status less frequently to reduce processing
const CLEANUP_INTERVAL = 1500;

// Simplified typing status information
interface TypingStatusInfo {
  isTyping: boolean;
  timestamp: number;
  userInfo?: {
    firstName?: string;
    lastName?: string;
    _id: string
  };
}

export interface WebSocketMsg {
  type: string;
  data: any;
  timestamp: number;
}

interface WebSocketState {
  socket: Socket | null;
  connectionStatus: ConnectionStatus;
  connectionError: string | null;
  currentServer: ServerKey;
  latency: number | null;
  missedPongs: number;
  newNotification: any | null;
  newMessage: Message & { userId?: string, userInfo?: Partial<UserInfo>, chatId?: string, assistantMessageId?: string } | null;
  chatUpdate: any | null;
  typingStatus: Record<string, Record<string, TypingStatusInfo>>;
  typingMap: Record<string, Record<string, boolean>>;
  lastTypingUpdate: Record<string, Record<string, number>>;
  onlineUsers: Set<string>;
  chatProgress: Record<string, ChatProgress | null>;
  socialSearchProgress: Record<string, SocialSearchProgress | null>;
  serverVersion: string | null;
  messages: WebSocketMsg[];
  authToken: string | null;
  reactionUpdates: Record<string, any>;
  humanVerificationUpdates: Record<string, any>;
}

interface WebSocketActions {
  setConnectionStatus: (status: ConnectionStatus) => void;
  setConnectionError: (error: string | null) => void;
  setServer: (server: ServerKey) => Promise<void>;
  setLatency: (latency: number | null) => void;
  setMissedPongs: (missedPongs: number) => void;
  setNewNotification: (notification: any | null) => void;
  setNewMessage: (message: Message & { userId?: string, userInfo?: Partial<UserInfo> } | null) => void;
  setChatUpdate: (update: any | null) => void;
  setTypingStatus: (data: {
    userId: string;
    chatId: string;
    isTyping: boolean;
    userInfo?: { firstName?: string; lastName?: string; _id: string }
  }) => void;
  setOnlineUsers: (callback: (prev: Set<string>) => Set<string>) => void;
  setSocket: (socket: Socket | null) => void;
  setServerVersion: (version: string | null) => void;
  isOnline: (userId: string) => boolean;
  requestUserStatus: (userId: string) => void;
  disconnect: () => void;
  reconnect: () => Promise<void>;
  setChatProgress: (progress: ChatProgress) => void;
  clearChatProgress: (chatId: string) => void;
  setSocialSearchProgress: (progress: SocialSearchProgress) => void;
  clearSocialSearchProgress: (userId: string) => void;
  addMessage: (msg: WebSocketMsg) => void;
  clearMessages: () => void;
  getMessagesByType: (type: string) => WebSocketMsg[];
  setAuthToken: (token: string | null) => void;
  getAuthToken: () => string | null;
  unified: (msg: WebSocketMsg) => void;
  setTyping: (chatId: string, userId: string, isTyping: boolean) => void;
  setReactionUpdate: (update: any) => void;
  setHumanVerificationUpdate: (update: any) => void;
}

type Store = WebSocketState & WebSocketActions;

const useWebSocketStore = create<Store>()(
  devtools(
    persist(
      (set, get) => {
        let cleanupIntervalId: NodeJS.Timeout | null = null;

        const clearCleanupInterval = () => {
          if (cleanupIntervalId) {
            clearInterval(cleanupIntervalId);
            cleanupIntervalId = null;
          }
        };

        // Start a cleanup interval to remove expired typing statuses - much simpler
        const startCleanupInterval = () => {
          clearCleanupInterval();
          cleanupIntervalId = setInterval(() => {
            const { typingMap, lastTypingUpdate } = get();
            const now = Date.now();
            let hasChanges = false;

            // Create a local copy to update
            const updatedTypingMap = { ...typingMap };

            // Just check the simplified map for expired typing indicators
            Object.keys(updatedTypingMap).forEach(chatId => {
              const chatTypers = updatedTypingMap[chatId];
              if (!chatTypers) return;

              // Checking last typing update times
              const lastUpdates = lastTypingUpdate[chatId] || {};

              Object.keys(chatTypers).forEach(userId => {
                const isTyping = chatTypers[userId];
                const lastUpdate = lastUpdates[userId] || 0;

                // If typing is stale, clear it
                if (isTyping && now - lastUpdate > TYPING_TIMEOUT) {
                  // Only log cleanup in development at reduced frequency
                  if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
                    // Uncomment for debugging typing status issues
                    // console.log(`[WebSocket] Auto-cleared typing status for user ${userId} in chat ${chatId}`);
                  }

                  hasChanges = true;
                  updatedTypingMap[chatId][userId] = false;
                }
              });
            });

            // Only update if changes were made
            if (hasChanges) {
              set({ typingMap: updatedTypingMap });

              // Only log cleanup in development at reduced frequency
              if (process.env.NODE_ENV === 'development' && Math.random() < 0.05) {
                // console.log('[WebSocket] Cleaned up expired typing statuses');
              }
            }
          }, CLEANUP_INTERVAL);
        };

        return {
          socket: null,
          connectionStatus: 'disconnected',
          connectionError: null,
          currentServer: 'production',
          latency: null,
          missedPongs: 0,
          newNotification: null,
          newMessage: null,
          chatUpdate: null,
          typingStatus: {},
          typingMap: {},
          lastTypingUpdate: {},
          onlineUsers: new Set(),
          chatProgress: {},
          socialSearchProgress: {},
          serverVersion: null,
          messages: [],
          authToken: null,
          reactionUpdates: {},
          humanVerificationUpdates: {},

          setConnectionStatus: (status: ConnectionStatus) => {
            const connectionStatus = typeof status === 'string' ?
              (status as ConnectionStatus) : 'disconnected';

            // Add debug message
            console.debug(`[WebSocket] Connection status changed: ${connectionStatus}`);
            set({ connectionStatus });
          },

          setConnectionError: (error) => set({ connectionError: error }),
          setLatency: (latency) => set({ latency }),
          setMissedPongs: (missedPongs) => set({ missedPongs }),
          setNewNotification: (notification) => set({ newNotification: notification }),
          setNewMessage: (message) => set({ newMessage: message }),
          setChatUpdate: (update) => set({ chatUpdate: update }),

          setTypingStatus: (data) => {
            const { userId, chatId, isTyping, userInfo } = data;

            if (!userId || !chatId) {
              console.warn('WebSocketStore: Missing userId or chatId for typing status update');
              return;
            }

            // Use the simplified setTyping method which is more efficient
            get().setTyping(chatId, userId, isTyping);
          },

          setOnlineUsers: (callback) => set(state => ({ onlineUsers: callback(state.onlineUsers) })),
          setSocket: (socket) => set({ socket }),
          setServerVersion: (version) => set({ serverVersion: version }),

          isOnline: (userId) => {
            return get().onlineUsers.has(userId);
          },

          requestUserStatus: (userId) => {
            const { socket } = get();
            if (socket) {
              socket.emit('message', {
                type: 'requestUserStatus',
                data: { userId },
                timestamp: Date.now()
              });
            }
          },

          disconnect: () => {
            const { socket } = get();
            if (socket) {
              socket.disconnect();
            }
            set({
              socket: null,
              connectionStatus: 'disconnected',
            });
            clearCleanupInterval();
          },

          reconnect: async () => {
            const { socket } = get();
            let attemptCount = 0;
            const maxAttempts = 5;
            const baseDelay = 500; // Start with 500ms delay

            const attemptReconnect = async (): Promise<void> => {
              try {
                // Always get a fresh token when reconnecting
                console.debug('[WebSocketStore] Getting fresh token for reconnection');
                const token = await getToken();

                if (!token) {
                  console.error('[WebSocketStore] Failed to get token for reconnection');
                  set({
                    connectionError: 'Authentication token not available for reconnection',
                    connectionStatus: 'disconnected'
                  });
                  throw new Error('Authentication token not available');
                }

                if (socket) {
                  // If socket exists, update its auth and reconnect
                  if (socket.auth && typeof socket.auth === 'object') {
                    (socket.auth as any).token = token;
                  }

                  if (socket.disconnected) {
                    socket.connect();
                    set({ connectionStatus: 'connecting' });
                    console.debug('[WebSocketStore] Reconnecting with fresh token');
                  } else {
                    console.debug('[WebSocketStore] Socket already connected, skipping reconnect');
                    set({ connectionStatus: 'connected' });
                  }
                } else {
                  // If no socket exists, we should rely on the WebSocketProvider to recreate it
                  console.warn('[WebSocketStore] Cannot reconnect: No socket instance available');
                  set({ connectionStatus: 'disconnected' });
                  throw new Error('No socket instance available');
                }

                // Check socket status after a short delay
                await new Promise<void>((resolve, reject) => {
                  const timeout = setTimeout(() => {
                    // If socket isn't connected after the timeout, reject
                    if (!socket || !socket.connected) {
                      reject(new Error('Socket failed to connect'));
                    } else {
                      resolve();
                    }
                  }, 2000); // 2 second timeout for reconnection

                  // If socket connects during timeout, resolve immediately
                  const onConnect = () => {
                    clearTimeout(timeout);
                    socket.off('connect', onConnect);
                    resolve();
                  };

                  // Listen for connection
                  if (socket) {
                    socket.once('connect', onConnect);
                  }
                });

                // If we got here, reconnection was successful
                set({ connectionStatus: 'connected' });
              } catch (error) {
                // If reconnection fails and we have more attempts, try again with exponential backoff
                attemptCount++;
                if (attemptCount < maxAttempts) {
                  const delay = baseDelay * Math.pow(2, attemptCount - 1); // Exponential backoff
                  console.warn(`[WebSocketStore] Reconnect attempt ${attemptCount} failed, retrying in ${delay}ms...`);
                  set({ connectionStatus: 'connecting' });
                  
                  await new Promise(resolve => setTimeout(resolve, delay));
                  return attemptReconnect();
                } else {
                  console.error('[WebSocketStore] Error during reconnect after max attempts:', error);
                  set({
                    connectionError: String(error),
                    connectionStatus: 'disconnected'
                  });
                  throw error;
                }
              }
            };

            return attemptReconnect();
          },

          setServer: async (server) => {
            if (server !== get().currentServer) {
              // Store the new server
              set({ currentServer: server });

              // The WebSocketProvider will handle reconnection with the new server
              const { disconnect } = get();
              disconnect();
            }
          },

          // Chat progress tracking
          setChatProgress: (progress) => {
            set(state => ({
              chatProgress: {
                ...state.chatProgress,
                [progress.chatId]: progress
              }
            }));
          },

          clearChatProgress: (chatId) => {
            set(state => {
              const newChatProgress = { ...state.chatProgress };
              delete newChatProgress[chatId];
              return { chatProgress: newChatProgress };
            });
          },

          // Social search progress tracking
          setSocialSearchProgress: (progress) => {
            set(state => ({
              socialSearchProgress: {
                ...state.socialSearchProgress,
                [progress.userId]: progress
              }
            }));
          },

          clearSocialSearchProgress: (userId) => {
            set(state => {
              const newProgress = { ...state.socialSearchProgress };
              delete newProgress[userId];
              return { socialSearchProgress: newProgress };
            });
          },

          // Message handling for the Socket.IO architecture
          addMessage: (msg) => {
            set(state => ({
              messages: [...state.messages, msg]
            }));

            // Call the unified message handler to process the message
            if (msg.type) {
              // Skip logging for heartbeat messages and server_version messages but still process them
              if (msg.type !== 'heartbeat' && msg.type !== 'server_version' && process.env.NODE_ENV === 'development') {
                // Only log in development mode and for non-routine messages
                if (['connection', 'disconnect', 'error', 'notification'].includes(msg.type)) {
                  console.log(`[WebSocket] ${msg.type} event received`);
                }
              }
              get().unified(msg);
            }
          },

          // Process unified messages
          unified: (msg: WebSocketMsg) => {
            // Type-specific handlers
            switch (msg.type) {
              case 'notification':
                get().setNewNotification(msg.data);
                break;

              case 'message':
                get().setNewMessage(msg.data);
                break;

              case 'chat_updated':
                get().setChatUpdate(msg.data);
                break;

              case 'typing_status':
                // Extract typing status data with fallbacks
                const data = msg.data || {};
                const userId = data.userId;
                const chatId = data.chatId;
                const isTyping = data.isTyping;
                const userInfo = data.userInfo;

                // For backward compatibility, still update the old typingStatus structure
                if (userId && chatId !== undefined && isTyping !== undefined) {
                  get().setTypingStatus({
                    userId: String(userId),
                    chatId: String(chatId),
                    isTyping,
                    userInfo
                  });

                  // Also update the new simplified typingMap
                  get().setTyping(String(chatId), String(userId), Boolean(isTyping));
                }
                break;

              case 'online_status':
                // Update online users set
                const { userId: onlineUserId, isOnline } = msg.data;
                if (onlineUserId) {
                  get().setOnlineUsers(prev => {
                    const newSet = new Set(prev);
                    if (isOnline) {
                      newSet.add(onlineUserId);
                    } else {
                      newSet.delete(onlineUserId);
                    }
                    return newSet;
                  });
                }
                break;

              case 'pong':
                const now = Date.now();
                const latency = now - (msg.data.timestamp || 0);
                set({
                  latency,
                  missedPongs: 0
                });
                break;

              case 'server_version':
                // Only update server version if it's different from current version
                // This prevents unnecessary state updates and re-renders
                if (msg.data && msg.data.version !== get().serverVersion) {
                  get().setServerVersion(msg.data.version);
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`WebSocketStore: Updated server version to ${msg.data.version}`);
                  }
                }
                break;
            }
          },

          clearMessages: () => {
            set({ messages: [] });
          },

          getMessagesByType: (type) => {
            return get().messages.filter(msg => msg.type === type);
          },

          setAuthToken: (token) => set({ authToken: token }),
          getAuthToken: () => get().authToken,

          // New simplified typing indicator
          setTyping: (chatId: string, userId: string, isTyping: boolean) => {
            // Ensure cleanup interval is running
            if (!cleanupIntervalId) {
              startCleanupInterval();
            }

            const { typingMap, lastTypingUpdate } = get();

            // Ensure the maps exist
            if (!typingMap[chatId]) {
              typingMap[chatId] = {};
            }

            if (!lastTypingUpdate[chatId]) {
              lastTypingUpdate[chatId] = {};
            }

            // Skip update if status hasn't changed
            if (typingMap[chatId][userId] === isTyping) {
              return;
            }

            // Update typing status and last update time
            const now = Date.now();
            lastTypingUpdate[chatId][userId] = now;

            // Update state
            set({
              typingMap: {
                ...typingMap,
                [chatId]: {
                  ...typingMap[chatId],
                  [userId]: isTyping
                }
              },
              lastTypingUpdate
            });

            // Only log important typing events in development at a reduced frequency
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
              // Uncomment for debugging typing status issues
              // console.log(`[WebSocket] User ${userId} is ${isTyping ? 'typing' : 'not typing'} in chat ${chatId}`);
            }
          },

          setReactionUpdate: (update) => {
            const { chatId, itemId } = update;
            set((state) => ({
              reactionUpdates: {
                ...state.reactionUpdates,
                [`${chatId}-${itemId}`]: update
              }
            }));
          },

          setHumanVerificationUpdate: (update) => {
            const { chatId, itemId } = update;
            set((state) => ({
              humanVerificationUpdates: {
                ...state.humanVerificationUpdates,
                [`${chatId}-${itemId}`]: update
              }
            }));
          },
        };
      },
      {
        name: 'websocket-storage',
        // Don't persist these fields
        partialize: (state) => ({
          currentServer: state.currentServer
        }),
      }
    )
  )
);

export default useWebSocketStore;

